import * as React from "react";
import { TextField } from "@mui/material";
import { Theme, darken, lighten } from "@mui/material/styles";
import ReactInputMask from "react-input-mask";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export type MaskConfigType = {
  mask: string;
  masks?: string[]; // Multiple masks for dynamic selection
  formatChars?: {
    [key: string]: string;
  };
  maskChar?: string | null;
};

type MaskWhenType = {
  field: string;
  values: string[];
  currentValue: string | null | undefined;
};

type MaskCondition = {
  field: string;
  operator: "in";
  values: string[];
};

type ConditionalMaskConfig = {
  mask: string;
  masks?: string[];
  when?: MaskCondition;
};

type InputStringMaskProps = {
  label: string;
  value: string;
  helperText?: string;
  error?: boolean;
  // Aviso que no bloquea: el helperText se pinta de advertencia. Un error lo tapa.
  warning?: boolean;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  customWidth?: number;
  maskConfig?: MaskConfigType;
  maskWhen?: MaskWhenType; // Conditional mask activation (single mask)
  conditionalMasks?: ConditionalMaskConfig[]; // Multiple conditional masks
  conditionValues?: Record<string, string | null | undefined>; // Current values for conditions
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  // "(Opcional)" (o el .nullText del campo) junto al label mientras está vacío.
  nullText?: string;
};

// Creamos un alias tipado para ReactInputMask
const InputMask = ReactInputMask as React.ComponentType<any>;

// Count how many digit placeholders (9) are in a mask
const countMaskDigits = (mask: string): number => {
  return (mask.match(/9/g) || []).length;
};

// Extract only digits from a value
const extractDigits = (value: string): string => {
  return value.replace(/\D/g, "");
};

// Check if masks are structurally compatible (same separator positions relative to edges)
const areMasksCompatible = (masks: string[]): boolean => {
  if (masks.length <= 1) return true;
  // Masks like "99-9999999-9" and "99-99999999-9" are NOT compatible
  // because the last dash position changes relative to the start
  // For now, consider masks compatible only if they have same length
  const lengths = masks.map((m) => m.length);
  return lengths.every((l) => l === lengths[0]);
};

// Format value according to mask pattern (for display-only formatting)
const formatWithMask = (value: string, masks: string[]): string => {
  const digits = extractDigits(value);
  if (digits.length === 0) return "";

  // Find best matching mask based on digit count
  const sortedMasks = [...masks].sort(
    (a, b) => countMaskDigits(a) - countMaskDigits(b)
  );
  let selectedMask = sortedMasks[sortedMasks.length - 1];
  for (const mask of sortedMasks) {
    if (countMaskDigits(mask) >= digits.length) {
      selectedMask = mask;
      break;
    }
  }

  // Apply mask pattern to digits
  let result = "";
  let digitIndex = 0;
  for (const char of selectedMask) {
    if (char === "9") {
      if (digitIndex < digits.length) {
        result += digits[digitIndex++];
      }
    } else {
      // Only add separator if we have digits after it
      if (digitIndex < digits.length) {
        result += char;
      }
    }
  }
  return result;
};

// Select the best mask based on the number of digits entered
const selectMask = (masks: string[], digitCount: number): string => {
  // Sort masks by digit count ascending
  const sortedMasks = [...masks].sort(
    (a, b) => countMaskDigits(a) - countMaskDigits(b)
  );

  // Use >= to stay on current mask until it's completely full
  for (const mask of sortedMasks) {
    if (countMaskDigits(mask) >= digitCount) {
      return mask;
    }
  }
  return sortedMasks[sortedMasks.length - 1];
};

export const InputStringMask: React.FC<InputStringMaskProps> = ({
  label,
  value,
  onChange,
  helperText = "",
  error = false,
  warning = false,
  autoFocus = false,
  customWidth = 500,
  maskConfig,
  maskWhen,
  conditionalMasks,
  conditionValues,
  disabled = false,
  hidden = false,
  description,
  nullText,
}) => {
  // Determine effective mask config based on conditions
  const effectiveMaskConfig = React.useMemo((): MaskConfigType | null => {
    // If using conditionalMasks, find the matching one
    if (conditionalMasks && conditionalMasks.length > 0 && conditionValues) {
      for (const cm of conditionalMasks) {
        if (cm.when) {
          const fieldValue = conditionValues[cm.when.field];
          if (fieldValue && cm.when.values.includes(fieldValue)) {
            return { mask: cm.mask, masks: cm.masks };
          }
        }
      }
      // No condition matched, no mask active
      return null;
    }
    // Otherwise use maskConfig directly
    return maskConfig || null;
  }, [conditionalMasks, conditionValues, maskConfig]);

  // Check if mask should be active based on condition (for single maskWhen)
  const isMaskActive = React.useMemo(() => {
    if (conditionalMasks) {
      // For conditional masks, active if we found a matching config
      return effectiveMaskConfig !== null;
    }
    if (!maskWhen) return true; // No condition = always active
    return maskWhen.values.includes(maskWhen.currentValue as string);
  }, [maskWhen, conditionalMasks, effectiveMaskConfig]);

  // Check if we have multiple incompatible masks
  const hasIncompatibleMasks = React.useMemo(() => {
    if (!effectiveMaskConfig) return false;
    return (
      effectiveMaskConfig.masks &&
      effectiveMaskConfig.masks.length > 1 &&
      !areMasksCompatible(effectiveMaskConfig.masks)
    );
  }, [effectiveMaskConfig]);

  // Calculate active mask based on input (for compatible masks)
  const activeMask = React.useMemo(() => {
    if (!effectiveMaskConfig) return "";
    if (!effectiveMaskConfig.masks || effectiveMaskConfig.masks.length <= 1) {
      return effectiveMaskConfig.mask;
    }
    const digits = extractDigits(value || "");
    return selectMask(effectiveMaskConfig.masks, digits.length);
  }, [value, effectiveMaskConfig]);

  // For incompatible masks, format the display value
  const displayValue = React.useMemo(() => {
    if (!hasIncompatibleMasks || !effectiveMaskConfig?.masks) return value;
    return formatWithMask(value || "", effectiveMaskConfig.masks);
  }, [value, hasIncompatibleMasks, effectiveMaskConfig]);

  // Antes de cualquier return: un hook no puede quedar detrás de if (hidden).
  const [focused, setFocused] = React.useState(false);

  if (hidden) return null;

  // "(Opcional)" junto al label mientras el campo está vacío y sin foco, como InputString.
  const isEmpty = value === "" || value === null || value === undefined;
  const shownLabel =
    nullText && isEmpty && !focused ? label + " (" + nullText + ")" : label;

  const formHelperTextProps =
    warning && !error
      ? {
          sx: {
            color: (theme: Theme) =>
              theme.palette.mode === "dark"
                ? lighten(theme.palette.warning.main, 0.5)
                : darken(theme.palette.warning.main, 0.5),
          },
        }
      : undefined;

  // Render regular TextField when mask condition is not met
  if (!isMaskActive) {
    return (
      <TextField
        autoFocus={autoFocus}
        size="small"
        inputProps={{ autoComplete: "one-time-code" }}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
        error={error}
        helperText={helperText}
        FormHelperTextProps={formHelperTextProps}
        sx={{ m: 1, mb: 1.5, width: customWidth }}
        label={
          <FieldLabelWithHelp label={shownLabel} description={description} />
        }
        InputLabelProps={description ? { shrink: true } : undefined}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    );
  }

  // For incompatible masks, use TextField with auto-formatting
  if (hasIncompatibleMasks && effectiveMaskConfig?.masks) {
    const maxDigits = Math.max(
      ...effectiveMaskConfig.masks.map(countMaskDigits)
    );
    return (
      <TextField
        autoFocus={autoFocus}
        size="small"
        inputProps={{ autoComplete: "one-time-code", maxLength: maxDigits + 5 }} // digits + separators
        value={displayValue}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          // Store formatted value (with dashes)
          const newValue = event.target.value;
          const formatted = formatWithMask(
            newValue,
            effectiveMaskConfig!.masks!
          );
          onChange(formatted);
        }}
        error={error}
        helperText={helperText}
        FormHelperTextProps={formHelperTextProps}
        sx={{ m: 1, mb: 1.5, width: customWidth }}
        label={
          <FieldLabelWithHelp label={shownLabel} description={description} />
        }
        InputLabelProps={description ? { shrink: true } : undefined}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    );
  }

  // El foco se escucha en InputMask y no en la TextField hija: react-input-mask
  // maneja onFocus y onBlur, y lanza si la hija los recibe.
  return (
    <InputMask
      disabled={disabled}
      mask={activeMask}
      value={value}
      maskChar={effectiveMaskConfig?.maskChar ?? null}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        onChange(event.target.value)
      }
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {() => (
        <TextField
          autoFocus={autoFocus}
          size="small"
          inputProps={{ autoComplete: "one-time-code" }}
          error={error}
          helperText={helperText}
          FormHelperTextProps={formHelperTextProps}
          sx={{ m: 1, mb: 1.5, width: customWidth }}
          label={
            <FieldLabelWithHelp label={shownLabel} description={description} />
          }
          InputLabelProps={description ? { shrink: true } : undefined}
        />
      )}
    </InputMask>
  );
};
